import React, { useState } from 'react';
import {
    Box, Card, CardContent, Tab, Tabs, TextField,
    Button, Typography, Alert, CircularProgress
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import './Login.css';

function Login() {
    const [tab, setTab] = useState(0); // 0 = sign in, 1 = create account
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const { login, register } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const redirectTo = location.state?.from || '/';

    const handleTabChange = (_, newValue) => {
        setTab(newValue);
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            if (tab === 0) {
                await login(email, password);
            } else {
                if (password.length < 8) {
                    setError('Password must be at least 8 characters.');
                    setLoading(false);
                    return;
                }
                if (!firstName.trim() || !lastName.trim()) {
                    setError('First name and last name are required.');
                    setLoading(false);
                    return;
                }
                await register(email, password, firstName.trim(), lastName.trim());
            }
            navigate(redirectTo, { replace: true });
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(detail || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box className="login-page">
            <Helmet>
                <title>{tab === 0 ? 'Sign In' : 'Create Account'} | Hockey Pipeline</title>
            </Helmet>

            <Card className="login-card" elevation={0}>
                <CardContent sx={{ p: 3 }}>
                    <div className="login-logo">
                        <img src="/logo@4x.png" alt="Hockey Pipeline" />
                    </div>

                    <Tabs
                        value={tab}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        sx={{
                            mb: 3,
                            '& .MuiTab-root': { color: '#6b7280', textTransform: 'none', fontSize: 15 },
                            '& .Mui-selected': { color: '#e2e8f0 !important' },
                            '& .MuiTabs-indicator': { backgroundColor: '#3b82f6' },
                        }}
                    >
                        <Tab label="Sign In" />
                        <Tab label="Create Account" />
                    </Tabs>

                    {/* Email / password form */}
                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        {tab === 1 && (
                            <>
                                <TextField
                                    fullWidth
                                    required
                                    label="First name"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    margin="normal"
                                    autoComplete="given-name"
                                    sx={fieldSx}
                                />
                                <TextField
                                    fullWidth
                                    required
                                    label="Last name"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    margin="normal"
                                    autoComplete="family-name"
                                    sx={fieldSx}
                                />
                            </>
                        )}
                        <TextField
                            fullWidth
                            required
                            label="Email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            margin="normal"
                            autoComplete="email"
                            sx={fieldSx}
                        />
                        <TextField
                            fullWidth
                            required
                            label="Password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            margin="normal"
                            autoComplete={tab === 0 ? 'current-password' : 'new-password'}
                            helperText={tab === 1 ? 'Minimum 8 characters' : ''}
                            sx={fieldSx}
                        />

                        {error && (
                            <Alert severity="error" sx={{ mt: 2, borderRadius: 2, backgroundColor: '#2d1515', color: '#fca5a5', '& .MuiAlert-icon': { color: '#f87171' } }}>
                                {error}
                            </Alert>
                        )}

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={loading}
                            sx={{
                                mt: 3,
                                py: 1.3,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontSize: 15,
                                fontWeight: 600,
                                backgroundColor: '#3b82f6',
                                '&:hover': { backgroundColor: '#2563eb' },
                            }}
                        >
                            {loading
                                ? <CircularProgress size={22} sx={{ color: '#fff' }} />
                                : tab === 0 ? 'Sign In' : 'Create Account'
                            }
                        </Button>
                    </Box>

                    {tab === 0 && (
                        <Typography
                            variant="body2"
                            align="center"
                            sx={{ mt: 2, color: '#6b7280', cursor: 'pointer', '&:hover': { color: '#e2e8f0' } }}
                            onClick={() => setTab(1)}
                        >
                            Don't have an account? <span style={{ color: '#3b82f6' }}>Sign up</span>
                        </Typography>
                    )}
                    {tab === 1 && (
                        <Typography
                            variant="body2"
                            align="center"
                            sx={{ mt: 2, color: '#6b7280', cursor: 'pointer', '&:hover': { color: '#e2e8f0' } }}
                            onClick={() => setTab(0)}
                        >
                            Already have an account? <span style={{ color: '#3b82f6' }}>Sign in</span>
                        </Typography>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        color: '#e2e8f0',
        borderRadius: 2,
        '& fieldset': { borderColor: '#2d3344' },
        '&:hover fieldset': { borderColor: '#4a5568' },
        '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
    },
    '& .MuiInputLabel-root': { color: '#6b7280' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#3b82f6' },
    '& .MuiFormHelperText-root': { color: '#6b7280' },
};

export default Login;
